import { mapPriceAdjustmentUserOverride } from 'apiConnector/quote/trade';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { TradeRequestPayload } from 'epic/common-form/Trade';
import * as fromDynamic from 'state/dynamic/infos/infos.selectors';
import { getDynamicFormPriceEnhancementMode } from 'state/dynamic/selectors';
import type { QuoteInfos } from 'state/quotes/pendings/pendings.model';
import * as fromPending from 'state/quotes/pendings/pendings.selectors';
import * as fromQuoteResults from 'state/quotes/results/results.selectors';
import { getQuoteType, isLoadingPreviousQuote } from 'state/quotes/selectors';
import { getStreamingSequence } from 'state/quotes/streaming/streaming.selector';
import { getTradeNotificationsContacts } from 'state/quotes/trades/trades.selectors';
import { getSide } from 'state/selectors/common/helper';
import { getFormQuoteId, getSidePanelAddonAction, getSidePanelTradeWay } from 'state/selectors/selectors';
import type { AppState } from 'state/state.model';
import { toExecutionBackToBack } from 'state/uiPanelState/tradeConfirmation/backToBack.utils';
import {
  getUserPreferencesLanguage,
  getUserPreferencesUnderlyingCodeType,
} from 'state/userPreferences/userPreferences.selectors';

import {
  getQuoteExternalIdByWorkspaceTab,
  getTradeConfirmationByQuoteId,
  getTradeInfosByWorkspaceTab,
  getUserPriceEnhancement,
  getUserSponsoFees,
} from './temp';
export function getDynamicFormInfos(state: AppState, tabId: string) {
  return fromDynamic.getFormInfos(state.dynamic, tabId);
}

export function getDynamicTabIdsFromQuoteId(state: AppState, quoteId: string) {
  return fromDynamic.getTabIdsByQuoteId(state.dynamic, quoteId);
}

export function getDynamicQuoteInfos(state: AppState, tabId: string, isForBooking: boolean): QuoteInfos | undefined {
  const quoteId = getDynamicFormQuoteId(state, tabId);

  if (isForBooking) {
    return (
      fromPending.getQuoteInfos(state.quotes.pendings, tabId) ??
      fromQuoteResults.getQuoteInfos(state.quotes.results, quoteId)
    );
  }

  return quoteId
    ? fromQuoteResults.getQuoteInfos(state.quotes.results, quoteId)
    : fromPending.getQuoteInfos(state.quotes.pendings, tabId);
}

export function getDynamicQuoteState(state: AppState, tabId: string) {
  const formInfos = getDynamicFormInfos(state, tabId);
  return formInfos?.quoteState;
}

export function getDynamicQuoteDetails(state: AppState, tabId: string) {
  const quoteId = getFormQuoteId(state, 'dynamic', tabId);
  return quoteId ? state.quotes.details[quoteId] : undefined;
}
export function getDynamicQuoteRequestStartingTimeInMillis(
  state: AppState,
  tabId: string,
  isForBooking: boolean = false,
) {
  const quoteInfos = getDynamicQuoteInfos(state, tabId, isForBooking);
  return quoteInfos?.quoteRequestStartingTimeInMillis;
}

export function getDynamicTechnicalError(
  state: AppState,
  tabId: string,
  isForBooking: boolean,
): ApiErrorMessage | undefined {
  const quote = getDynamicQuoteInfos(state, tabId, isForBooking);
  const trade = getTradeInfosByWorkspaceTab(state, 'dynamic', tabId);
  return trade?.technicalError ?? quote?.technicalError;
}

export function getDynamicFormValidationErrors(state: AppState, tabId: string) {
  return fromDynamic.getFormValidationErrors(state.dynamic, tabId);
}

export function getDynamicFormQuoteId(state: AppState, tabId: string): string | undefined {
  return fromDynamic.getFormQuoteId(state.dynamic, tabId);
}
export function getDynamicQuoteResult(state: AppState, tabId: string) {
  const quoteId = getDynamicFormQuoteId(state, tabId);
  return fromQuoteResults.getQuoteResult(state.quotes.results, quoteId);
}

export function getDynamicPreviousQuoteIsLoading(state: AppState, tabId: string) {
  const quoteId = getDynamicFormQuoteId(state, tabId);
  return isLoadingPreviousQuote(state, quoteId);
}

export function getDynamicInstrumentName(state: AppState, tabId: string) {
  const quoteDetails = getDynamicQuoteDetails(state, tabId);
  return quoteDetails?.InstrumentName ?? getDynamicFormInfos(state, tabId)?.instrumentName;
}
export function getDynamicInstrumentContractName(state: AppState, tabId: string) {
  const quoteDetails = getDynamicQuoteDetails(state, tabId);
  return quoteDetails?.InstrumentContractName ?? getDynamicFormInfos(state, tabId)?.instrumentContractName;
}

export function getDynamicWrapper(state: AppState, tabId: string) {
  const quoteDetails = getDynamicQuoteDetails(state, tabId);
  return quoteDetails?.Wrapper;
}

export function getDynamicTradeRequest(state: AppState, tabId: string) {
  const quote = getDynamicQuoteResult(state, tabId)!;
  const { quoteId: internalQuoteId } = getDynamicFormInfos(state, tabId)!;
  const quoteType = getQuoteType(state, internalQuoteId);
  const externalQuoteId = getQuoteExternalIdByWorkspaceTab(state, 'dynamic', tabId)!;
  const mode = getDynamicFormPriceEnhancementMode(state, tabId);
  const priceEnhancement = getUserPriceEnhancement(state, 'dynamic', tabId);
  const sponsoFees = getUserSponsoFees(state, 'dynamic', tabId);
  const priceAdjustmentUserOverride = mapPriceAdjustmentUserOverride(mode, priceEnhancement);
  const tradeInfos = getTradeInfosByWorkspaceTab(state, 'dynamic', tabId);
  const defaultUnderlyingCodeType = getUserPreferencesUnderlyingCodeType(state);
  const languageCode = getUserPreferencesLanguage(state);
  const instrumentName = getDynamicInstrumentName(state, tabId);
  const instrumentContractName = getDynamicInstrumentContractName(state, tabId);
  const wrapper = getDynamicWrapper(state, tabId);
  const tradeWay = getSidePanelTradeWay(state, tabId);
  const { priceEnhancement: defaultPriceEnhancement } = quote;
  const executionOnly = quoteType === 'ExecOnly' || quoteType === 'AdvancedExecOnly';
  const side = getSide(quote.isExecOnlyAddon, tradeWay);
  const addonAction = getSidePanelAddonAction(state, tabId, 'dynamic');
  const tradeNotifications = getTradeNotificationsContacts(state, internalQuoteId);
  const tradeRequest: TradeRequestPayload = {
    side,
    externalQuoteId: externalQuoteId!,
    internalQuoteId: internalQuoteId!,
    productSubType: quote.productSubType,
    wrapper: wrapper,
    screenMode: 'NewDynamic',
    issuePrice: defaultPriceEnhancement?.issuePrice!,
    priceAdjustmentUserOverride,
    sponsoFees,
    executionOnly,
    comment: tradeInfos.comment,
    externalOrderId: tradeInfos.executionOnlyDealId,
    defaultUnderlyingCodeType,
    languageCode,
    notifications: tradeNotifications,
    backToBack: toExecutionBackToBack(getTradeConfirmationByQuoteId(state, internalQuoteId)),
    addonAction,
    instrumentName: instrumentName,
    instrumentContractName: instrumentContractName,
    streamingSequence: getStreamingSequence(state, tabId),
  };

  return tradeRequest;
}
